import type { SyntheticEvent } from "react";
import type { TradeInAemNavStep } from "./constants";
import { type TradeInAemLabelType, tradeInAemNavStepLabels } from "./labels";
import type { QueryType } from "../../common-deprecated/server/types";
import { objectToQueryString } from "../../common-deprecated/utils";
import { HistoryType, pushQueryParams } from "../../common-deprecated/History";
import type { CommonSettingsType } from "../../common-deprecated/settings/fetchCommonSettings";
import type { TradeInAemDispatchType } from "../redux/store";
import { isDevMode } from "./utils";
import type { TradeInCsl } from "../csl/TradeInCsl";

export type TradeInAemNavStepType = {
    label: TradeInAemLabelType;
    stepCount: number;
    href?: string;
    onClick?: (event: React.SyntheticEvent) => void;
    type?: TradeInAemNavStep;
};

/**
 * Returns the config of the navigation steps
 */
export const getNavStepsConfigs = (
    csl: TradeInCsl,
    currentStep: TradeInAemNavStep,
    stepList: TradeInAemNavStep[],
    dispatch: TradeInAemDispatchType,
    commonSettings: CommonSettingsType,
): TradeInAemNavStepType[] => {
    const devMode = isDevMode(commonSettings);

    const currentStepIndex = getNavStepIndex(csl, currentStep);

    return stepList.map((step, index) => {
        const internalLink = getInternalLink(csl, currentStep, dispatch, commonSettings, step);
        // You can only click on previous steps except when devMode is true you can navigate to all steps
        const isClickable = index < currentStepIndex || devMode;
        const onClick = isClickable ? internalLink.onClick : undefined;
        const href = isClickable ? internalLink.href : undefined;

        return {
            label: tradeInAemNavStepLabels[step],
            stepCount: index + 1,
            onClick,
            href,
            type: step,
        };
    });
};

/**
 * Returns the visual indication of a steps "number" as an index (0 based).
 */
export const getNavStepIndex = (csl: TradeInCsl, currentStep: TradeInAemNavStep): number => {
    const stepList = csl.enabledNavSteps;

    return stepList.findIndex((step) => currentStep === step);
};

/**
 * Retrieve the 'next' or 'previous' step based on the current nav params.
 *
 * Can be used on its own if there is logic which needs to know what the actual next step is.
 * This logic is also supported in getTradeInInternalLink.
 */
export const getStepSibling = (
    csl: TradeInCsl,
    currentStep: TradeInAemNavStep,
    target: "next" | "prev",
): TradeInAemNavStep => {
    const enabledSteps = csl.enabledNavSteps;
    const currentIndex = enabledSteps.indexOf(currentStep);
    const nextIndex = currentIndex + (target === "prev" ? -1 : 1);

    let nextStep: TradeInAemNavStep;
    if (enabledSteps[nextIndex]) {
        nextStep = enabledSteps[nextIndex];
    } else {
        // Fallback, this is technically 'wrong' but in most cases the URL will either not be used or hidden.
        // Extra logic to hide the previous step can be found here: showBackCtaOnStepComponent.
        nextStep = currentStep;
    }

    return nextStep;
};
export type TradeInLinkPropsType = { onClick?: (event?: React.SyntheticEvent) => void; href?: string };

/**
 * Retrieve href/onClick properties for an internal Trade In link.
 */
export const getInternalLink = (
    csl: TradeInCsl,
    currentStep: TradeInAemNavStep,
    dispatch: TradeInAemDispatchType,
    commonSettings: CommonSettingsType,
    target: TradeInAemNavStep | "next" | "prev",
): TradeInLinkPropsType => {
    const step = target === "next" || target === "prev" ? getStepSibling(csl, currentStep, target) : target;
    const devMode = isDevMode(commonSettings);

    let newParams: QueryType = commonSettings.query;

    if (devMode) {
        newParams = {
            ...commonSettings.query,
            tradeInStep: step,
        };
    }

    return {
        href: `?${objectToQueryString(newParams)}`,
        onClick: (event?: SyntheticEvent): void => {
            if (event) event.preventDefault();

            if (currentStep === target) return;

            csl.onStepTransition(step);
            pushQueryParams(newParams, HistoryType.Tradein);
        },
    };
};
